import {api} from "@/shared/api";

import {DownloadStatus} from "@/shared/ui/downloadStatus";
import {TimeAccountBase, TimeAccountDashboardConfig} from "./timeAccountBase";
import {CurrentTimeAccountTable} from "./currentTimeAccountTable";
import {Combobox} from "@/shared/ui/combobox";
import {GroupDropdown} from "@/shared/ui/groupDropdown";
import {ButtonRadioGroup} from "@/shared/ui/buttonRadioGroup";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {Group, TimeAccountDashboardBalanceMode, User} from "@/shared/timr";
import {ExportType} from "@/shared/timrTypes";

export class CurrentTimeAccountDashboardPage extends TimeAccountBase {
	public static readonly GROUP_TIME_ACCOUNT = "currentTimeAccountGroup";
	private groupDropdown: GroupDropdown;
	private userCombobox: Combobox<User>;

	private currentTimeAccountTable: CurrentTimeAccountTable;
	private exportButton: JQuery;
	private exportLoadingButton: JQuery;

	private filterUsersWithInitializedTimeAccountRequest: JQueryXHR;

	constructor(config: TimeAccountDashboardConfig) {
		super(config);
		config.timeAccountViewType = "CURRENT_BALANCES";

		this.userCombobox = new Combobox({
			items: config.usersWithInitializedTimeAccount,
			formatter: (user) => {
				return user.fullname;
			},
			idElement: $("#currentTimeAccountDashboardFilterUserId"),
			filterElement: $("#currentTimeAccountDashboardFilterUserFilter"),
			caretElement: $("#currentTimeAccountDashboardFilterUserCaret"),
			emptyMessage: messages["user.filter.all"],
			clearEntryMessage: messages["user.filter.all"]
		});
		const validateWorkingTimes = globals.validateWorkingTime || globals.validateWorkingTimeOverlaps;

		this.userCombobox.setSelectedItemById(BrowserStorageSettings.getTimeAccountDashboardUserFilter(CurrentTimeAccountDashboardPage.GROUP_TIME_ACCOUNT), config.users.length === 1);
		$(this.userCombobox).on(Combobox.EVENT_CHANGED, () => {
			const userId = this.userCombobox.getSelectedItem()?.id;
			BrowserStorageSettings.setTimeAccountUserFilter(CurrentTimeAccountDashboardPage.GROUP_TIME_ACCOUNT, userId);
			void this.currentTimeAccountTable.filterByModeAndGroupAndUser(this.balanceModeControl.getValue(), this.groupDropdown.getSelectedId(), userId);
			if (validateWorkingTimes) {
				this.validateWorkingTimes(this.groupDropdown.getSelectedId(), userId, true);
			}
		});

		this.groupDropdown = new GroupDropdown({
			rootElement: $("#currentTimeAccountDashboardFilterGroupDropdown"),
			allowSelectionOfNotAssigned: false,
			emptyMessage: messages["group.filter.all"],
			clearEntryMessage: messages["user.filter.all"]
		});
		const defaultGroup = BrowserStorageSettings.getTimeAccountGroupFilter(CurrentTimeAccountDashboardPage.GROUP_TIME_ACCOUNT);
		if (defaultGroup) {
			this.groupDropdown.setSelection(defaultGroup.id, defaultGroup.name, defaultGroup.breadCrumbs, defaultGroup);
		}
		this.filterUsersWithInitializedTimeAccount(defaultGroup?.id);
		$(this.groupDropdown).on(GroupDropdown.EVENT_SELECTED, (_, group: Group) => {
			BrowserStorageSettings.setTimeAccountGroupFilter(CurrentTimeAccountDashboardPage.GROUP_TIME_ACCOUNT, group);
			void this.currentTimeAccountTable.filterByModeAndGroupAndUser(this.balanceModeControl.getValue(), group?.id, this.getSelectedUserId());
			this.filterUsersWithInitializedTimeAccount(group?.id);
			if (validateWorkingTimes) {
				this.validateWorkingTimes(group?.id, this.userCombobox.getSelectedItem()?.id, true);
			}
		});
		if (validateWorkingTimes) {
			this.validateWorkingTimes(this.groupDropdown.getSelectedId(), this.userCombobox.getSelectedItem()?.id, true);
		}
		this.initBalanceModeControl();
		this.currentTimeAccountTable = new CurrentTimeAccountTable({
			element: $("#currentTimeAccountTable"),
			initialFilter: CurrentTimeAccountTable.buildFilterParams(
				BrowserStorageSettings.getTimeAccountDashboardBalanceMode(CurrentTimeAccountDashboardPage.GROUP_TIME_ACCOUNT) || "TODAY",
				this.groupDropdown.getSelectedId(),
				this.getSelectedUserId()
			),
			orderable: false,
			clampNote: false,
			allowExpand: false,
			showOvertimesColumn: config.showOvertimesColumn,
			showTravellingTimesColumn: config.showTravellingTimesColumn,
			showFlexTimeIndicatorColumn: config.showFlexTimeIndicatorColumn
		});

		this.initExport();
	}

	protected initBalanceModeControl() {
		this.balanceModeControl = ButtonRadioGroup.typed<TimeAccountDashboardBalanceMode>({
			value: "YESTERDAY",
			button: document.getElementById("currentTimeAccountDashboardBalanceModeYesterdayButton")
		}, {
			value: "TODAY",
			button: document.getElementById("currentTimeAccountDashboardBalanceModeTodayButton")
		}).onValueChanged((mode: TimeAccountDashboardBalanceMode) => {
			void this.currentTimeAccountTable?.filterByModeAndGroupAndUser(mode, this.groupDropdown.getSelectedId(), this.userCombobox.getSelectedItem()?.id);
			BrowserStorageSettings.setTimeAccountDashboardBalanceMode(CurrentTimeAccountDashboardPage.GROUP_TIME_ACCOUNT, mode);
		});
		this.balanceModeControl.setValue(BrowserStorageSettings.getTimeAccountDashboardBalanceMode(CurrentTimeAccountDashboardPage.GROUP_TIME_ACCOUNT) || "TODAY");
	}

	private initExport() {
		this.exportButton = $("#currentTimeAccountDashboardExportButton").show();
		this.exportLoadingButton = $("#currentTimeAccountDashboardExportButtonLoadingIndicator").hide();

		this.currentTimeAccountTable.config.element.on("processing.dt", (_e, _settings, processing) => {
			if (processing) {
				Button.setLoading(this.exportButton);
			} else {
				Button.resetLoading(this.exportButton);
			}
		});

		$("#currentTimeAccountDashboardExportDropdown").find("a").click((e) => {
			const exportType = $(e.currentTarget).data("exportType") as ExportType;
			if (this.currentTimeAccountTable.getRowCount() === 0) {
				const modal = new BootstrapModal.Modal({
					title: "reports.export.confirm.title",
					message: messages["reports.export.confirm.message"],
					css: {
						header: "bg-primary"
					},
					buttons: [{
						text: "reports.export.button.continue",
						action: (m) => {
							this.export(exportType);
							m.close();
						},
						css: "btn-primary"
					}, {
						text: "confirmation.cancel",
						css: "btn-default",
						action: (m) => {
							m.close();
						}
					}]
				});
				modal.show();
			} else {
				this.export(exportType);
			}
		});
	}

	private export(exportType: ExportType) {
		const balanceMode = this.balanceModeControl.getValue();
		const params = CurrentTimeAccountTable.buildFilterParams(balanceMode, this.groupDropdown.getSelectedId(), this.getSelectedUserId());
		params.push({name: "exportFileType", value: exportType});

		void api("export/currentTimeAccount.json")
			.post(params)
			.done(data => {
				if (data.exportJobId) {
					new DownloadStatus({
						exportJobId: data.exportJobId,
						exportStatusBaseUrl: "export/status.json",
						exportType: exportType,
						loadingMessage: messages[`downloadStatus.loadingMessage.reports.${exportType}`],
						allowCancellation: true
					});
				}
			});
	}

	private getSelectedUserId(): number {
		const user = this.userCombobox.getSelectedItem();
		return user ? user.id : null;
	}

	private filterUsersWithInitializedTimeAccount(groupId: number): void {
		this.filterUsersWithInitializedTimeAccountRequest?.abort();
		this.filterUsersWithInitializedTimeAccountRequest = api("timeaccounts/getUsersForTimeAccount.json")
			.get({
				groupId: groupId,
				activeUsersOnly: true
			})
			.done((data: { usersWithInitializedTimeAccount: User[] }) => {
				if (data?.usersWithInitializedTimeAccount) {
					this.userCombobox.updateItems(data.usersWithInitializedTimeAccount);
				}
			});
	}

	protected getPreviewTimeSheetUserId(): number {
		return this.userCombobox.getSelectedItem()?.id;
	}

	protected onBalanceModeChanged(mode: TimeAccountDashboardBalanceMode) {
		BrowserStorageSettings.setTimeAccountDashboardBalanceMode(CurrentTimeAccountDashboardPage.GROUP_TIME_ACCOUNT, mode);
		void this.getCurrentTimeAccountVacationData(mode, globals.userId);
		this.setPreviewTimeSheetDialogDate(mode);
		this.updateForwardPreviewDateButtonTitle(mode);
	}
}
